import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, MessageSquare, Save } from "lucide-react";

interface SiteMessage {
  id: string;
  message_key: string;
  label: string;
  content: string;
}

interface Props {
  sessionToken: string;
}

const MessageManagement = ({ sessionToken }: Props) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [messages, setMessages] = useState<SiteMessage[]>([]);
  const { toast } = useToast();

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "list_site_messages", sessionToken },
      });
      if (res.data?.messages) setMessages(res.data.messages);
    } catch {
      toast({ title: "Failed to load messages", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMessages(); }, []);

  const handleContentChange = (id: string, newContent: string) => {
    setMessages(prev => prev.map(m => m.id === id ? { ...m, content: newContent } : m));
  };

  const handleSave = async (message: SiteMessage) => {
    setSaving(message.id);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "update_site_message", sessionToken, messageId: message.id, content: message.content },
      });
      if (res.data?.error) throw new Error(res.data.error);
      toast({ title: `${message.label} updated!` });
    } catch (err) {
      const error = err as Error;
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Site Message Management
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {messages.map((message) => (
          <div key={message.id} className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
            <div className="flex-1 space-y-1 w-full">
              <Label className="text-sm font-medium">{message.label}</Label>
              <Textarea
                value={message.content}
                onChange={(e) => handleContentChange(message.id, e.target.value)}
                placeholder="Enter message content..."
                className="text-sm min-h-[60px]"
                rows={2}
              />
            </div>
            <Button
              size="sm"
              onClick={() => handleSave(message)}
              disabled={saving === message.id}
              className="shrink-0"
            >
              {saving === message.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default MessageManagement;
